// src/App.js
import React, { useState, useEffect, lazy, Suspense } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
} from "react-router-dom";
import ThemeProviderWrapper from "./ThemeProviderWrapper";
import { themes } from "./themes";
import { ResponsiveProvider } from "./contexts/ResponsiveContext";
import Header from "./components/Header";
import AuthForm from "./components/AuthForm";
import ConfirmSignUp from "./components/ConfirmSignUp";
import ProtectedRoute from "./components/ProtectedRoute";
import Loading from "./components/Loading";
import AuthGuard from "./components/AuthGuard";
import ThemeBootstrapper from "./components/ThemeBootstrapper";
import CssBaseline from "@mui/material/CssBaseline";
import {
  clearAuthState,
  isAuthenticated as checkAuth,
  isTokenExpired,
} from "./utils/authUtils";
import {
  preventAWSRedirects,
  clearAllAWSState,
} from "./utils/awsRedirectPrevention";
import "./App.css";

// Lazy-loaded pages
const LandingPage       = lazy(() => import("./components/LandingPage"));
const GalleryPage       = lazy(() => import("./components/GalleryPage"));
const AboutPage         = lazy(() => import("./components/AboutPage"));
const ContactPage       = lazy(() => import("./components/ContactPage"));
const ArtistDashboard   = lazy(() => import("./components/ArtistDashboard"));
const UploadArtworkPage = lazy(() => import("./components/UploadArtworkPage"));
const ManageGalleryPage = lazy(() => import("./components/ManageGalleryPage"));
const BrandManagementStudio = lazy(() =>
  import("./components/brand-kit/BrandManagementStudio")
);
const EditAboutPage = lazy(() => import("./components/EditAboutPage"));
const BlogPage = lazy(() => import("./components/BlogPage"));
const BlogsPage = lazy(() => import("./components/BlogsPage"));
const VideoViewerPage = lazy(() => import("./components/VideoViewerPage"));
const StorePage = lazy(() => import("./components/StorePage"));
const ManageStorePage = lazy(() => import("./components/ManageStorePage"));
const ManageCollectionsPage = lazy(() => import("./components/ManageCollectionsPage"));
const CheckoutSuccessPage = lazy(() => import("./components/CheckoutSuccessPage"));
const CheckoutCancelPage = lazy(() => import("./components/CheckoutCancelPage"));

// Prevents Safari swipe-back navigation from logging out authenticated users
const SwipeNavigationBlocker = ({ isAuthenticated }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const currentPathRef = React.useRef(location.pathname);
  const isBlockingRef = React.useRef(false);

  useEffect(() => {
    currentPathRef.current = location.pathname;
    isBlockingRef.current = false;
  }, [location.pathname]);

  useEffect(() => {
    if (!isAuthenticated) return;

    const publicPages = ["/login", "/register", "/confirm-signup"];
    if (publicPages.includes(location.pathname)) return;

    const handlePopState = () => {
      if (isBlockingRef.current) return;
      const targetPath = currentPathRef.current;
      if (window.location.pathname !== targetPath) {
        isBlockingRef.current = true;
        window.history.replaceState(null, "", targetPath);
        navigate(targetPath, { replace: true });
        setTimeout(() => { isBlockingRef.current = false; }, 100);
      }
    };

    window.history.pushState({ preventBack: true }, "", currentPathRef.current);
    window.addEventListener("popstate", handlePopState, { passive: true });
    return () => window.removeEventListener("popstate", handlePopState);
  }, [isAuthenticated, location.pathname, navigate]);

  return null;
};

const App = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const [theme, setTheme] = useState(() => {
    try {
      const saved = localStorage.getItem("THEME");
      if (saved) return JSON.parse(saved);
    } catch {
      // ignore
    }
    return themes.sage;
  });

  useEffect(() => {
    const cleanup = preventAWSRedirects();
    clearAllAWSState();
    clearAuthState();
    setIsAuthenticated(false);
    return cleanup;
  }, []);

  // Token expiration + inactivity monitoring
  useEffect(() => {
    if (!isAuthenticated) return;

    const INACTIVITY_TIMEOUT = 30 * 60 * 1000;

    const checkInterval = setInterval(() => {
      const token = localStorage.getItem("jwtToken");
      if (token && isTokenExpired(token)) {
        clearAuthState();
        setIsAuthenticated(false);
        window.location.replace("/login");
        return;
      }
      const lastActivity = localStorage.getItem("lastActivityTime");
      if (lastActivity && Date.now() - parseInt(lastActivity, 10) > INACTIVITY_TIMEOUT) {
        clearAuthState();
        setIsAuthenticated(false);
        window.location.replace("/login");
      }
    }, 30000);

    const updateActivity = () => {
      if (isAuthenticated) localStorage.setItem("lastActivityTime", Date.now().toString());
    };
    const events = ["mousedown", "mousemove", "keypress", "scroll", "touchstart", "click"];
    events.forEach((e) => window.addEventListener(e, updateActivity, { passive: true }));

    return () => {
      clearInterval(checkInterval);
      events.forEach((e) => window.removeEventListener(e, updateActivity));
    };
  }, [isAuthenticated]);

  const handleThemeChange = (newTheme) => {
    setTheme(newTheme);
    localStorage.setItem("THEME", JSON.stringify(newTheme));
  };

  return (
    <Router>
      <ResponsiveProvider>
        <ThemeProviderWrapper theme={theme}>
          <CssBaseline />
          <ThemeBootstrapper
            isAuthenticated={isAuthenticated}
            onThemeLoaded={handleThemeChange}
          />
          <SwipeNavigationBlocker isAuthenticated={isAuthenticated} />
          <AuthGuard>
            <Header
              isAuthenticated={isAuthenticated}
              setIsAuthenticated={setIsAuthenticated}
            />
            <Suspense fallback={<Loading />}>
              <Routes>
                {/* ── Public routes ── */}
                <Route path="/" element={<LandingPage />} />
                <Route path="/gallery" element={<GalleryPage />} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/blog" element={<BlogPage />} />
                <Route
                  path="/blogs"
                  element={
                    <ProtectedRoute isAuthenticated={isAuthenticated}>
                      <BlogsPage />
                    </ProtectedRoute>
                  }
                />
                {/* The Blogs page's "Open in Assets Studio" chip targets this
                    route. Peaceful Oasis has no Assets Studio of its own, so
                    redirect back to /blogs instead of 404'ing. */}
                <Route
                  path="/assets-studio-v2"
                  element={<Navigate to="/blogs" replace />}
                />
                <Route path="/video" element={<VideoViewerPage />} />
                <Route path="/store" element={<StorePage />} />
                <Route path="/checkout/success" element={<CheckoutSuccessPage />} />
                <Route path="/checkout/cancel" element={<CheckoutCancelPage />} />

                {/* ── Auth routes ── */}
                <Route
                  path="/login"
                  element={<AuthForm setIsAuthenticated={setIsAuthenticated} />}
                />
                <Route
                  path="/register"
                  element={<AuthForm setIsAuthenticated={setIsAuthenticated} />}
                />
                <Route path="/confirm-signup" element={<ConfirmSignUp />} />

                {/* ── Protected routes (artist admin) ── */}
                <Route
                  path="/dashboard"
                  element={
                    <ProtectedRoute isAuthenticated={isAuthenticated}>
                      <ArtistDashboard />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/upload"
                  element={
                    <ProtectedRoute isAuthenticated={isAuthenticated}>
                      <UploadArtworkPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/manage-gallery"
                  element={
                    <ProtectedRoute isAuthenticated={isAuthenticated}>
                      <ManageGalleryPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/brand-studio"
                  element={
                    <ProtectedRoute isAuthenticated={isAuthenticated}>
                      <BrandManagementStudio onChangeTheme={handleThemeChange} />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/edit-about"
                  element={
                    <ProtectedRoute isAuthenticated={isAuthenticated}>
                      <EditAboutPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/manage-store"
                  element={
                    <ProtectedRoute isAuthenticated={isAuthenticated}>
                      <ManageStorePage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/manage-collections"
                  element={
                    <ProtectedRoute isAuthenticated={isAuthenticated}>
                      <ManageCollectionsPage />
                    </ProtectedRoute>
                  }
                />

                {/* ── Catch-all ── */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Suspense>
          </AuthGuard>
        </ThemeProviderWrapper>
      </ResponsiveProvider>
    </Router>
  );
};

export default App;
